import {
	Object3D,
	MeshStandardMaterial,
	Mesh,
	Vector2,
	CircleGeometry,
	Vector3,
	Vector2Tuple,
	Box3,
} from 'three';
import { RectangleResult } from '../types';
import { bbox } from '@turf/turf';
import { Feature, Polygon } from 'geojson';
import clipping from 'polygon-clipping';

export abstract class RenderUtils {
	static disposeObject(object: Object3D) {
		object.removeFromParent();

		object.traverse((object) => {
			if (RenderUtils.isMesh(object)) {
				if (object.geometry) object.geometry.dispose();

				if (object.material) {
					if (Array.isArray(object.material)) {
						object.material.forEach((material) => material.dispose());
					} else {
						object.material.dispose();
					}
				}
			}
		});

		object.clear();
	}

	static disposeMaterial(object: Object3D) {
		if (!RenderUtils.isMesh(object)) return;

		if (Array.isArray(object.material)) {
			for (const material of object.material) {
				const mat = material as MeshStandardMaterial;

				mat.dispose();
				mat.map?.dispose();
				mat.normalMap?.dispose();
				mat.roughnessMap?.dispose();
				mat.metalnessMap?.dispose();
			}
		} else {
			const mat = object.material as MeshStandardMaterial;

			mat.dispose();
			mat.map?.dispose();
			mat.normalMap?.dispose();
			mat.roughnessMap?.dispose();
			mat.metalnessMap?.dispose();
		}
	}

	static isMesh(object: Object3D): object is Mesh {
		return object instanceof Mesh;
	}

	static computePolygonArea(vertices: Vector2[]) {
		let area = 0;
		const n = vertices.length;

		for (let i = 0; i < n; i++) {
			const current = vertices[i];
			const next = vertices[(i + 1) % n];
			area += current.x * next.y - next.x * current.y;
		}

		return Math.abs(area) / 2;
	}

	static symmetricRound(n: number): number {
		return Math.sign(n) * Math.round(Math.abs(n));
	}

	static ccw(a: Vector2, b: Vector2, c: Vector2) {
		return (c.y - a.y) * (b.x - a.x) > (b.y - a.y) * (c.x - a.x);
	}

	static generateCircleUVs(geometry: CircleGeometry, radius: number) {
		const scale = radius * 2;

		const uvs = geometry.attributes.uv;
		for (let i = 0; i < uvs.count; i++) {
			const x = uvs.getX(i);
			const y = uvs.getY(i);

			uvs.setXY(i, x * scale, y * scale);
		}
	}

	static calcEndPoint(start: Vector3, end: Vector3, length: number) {
		const dir = new Vector3()
			.subVectors(end, start)
			.normalize()
			.multiplyScalar(length);

		return start.clone().add(dir);
	}

	private static simpleTranslate(
		multiPolyCoords: Vector2Tuple[][][],
		dx: number,
		dy: number
	): Vector2Tuple[][][] {
		return multiPolyCoords.map((poly) =>
			poly.map((ring) => ring.map((point) => [point[0] + dx, point[1] + dy]))
		);
	}

	static getHorizontalAxis(box: Box3) {
		const size = new Vector3();
		box.getSize(size);

		return size.x > size.z ? 'x' : 'z';
	}

	static areBoxesParallel(boxA: Box3, boxB: Box3) {
		const axisA = RenderUtils.getHorizontalAxis(boxA);
		const axisB = RenderUtils.getHorizontalAxis(boxB);

		return axisA === axisB;
	}

	static doesRectangleFit(
		mainPolygon: Feature<Polygon>,
		rectWidth: number,
		rectHeight: number
	) {
		if (rectWidth <= 0 || rectHeight <= 0) return true;

		const mainPolyCoords = mainPolygon.geometry.coordinates as Vector2Tuple[][];

		const translatedHorizontallyCoords = this.simpleTranslate(
			[mainPolyCoords],
			-rectWidth,
			0
		)[0] as Vector2Tuple[][];

		const horizontalIntersection = clipping.intersection(
			mainPolyCoords,
			translatedHorizontallyCoords
		);

		if (horizontalIntersection.length === 0) {
			return false;
		}

		const translatedVerticallyCoords = this.simpleTranslate(
			horizontalIntersection,
			0,
			-rectHeight
		);

		const finalIntersection = clipping.intersection(
			horizontalIntersection,
			translatedVerticallyCoords
		);

		return finalIntersection.length > 0;
	}

	static findLargestForAspectRatio(
		mainPolygon: Feature<Polygon>,
		ratio: number,
		maxPossibleHeight: number
	): RectangleResult {
		let minH = 0;
		let maxH = maxPossibleHeight;
		let bestFoundHeight = 0;

		for (let i = 0; i < 100; i++) {
			const midH = (minH + maxH) / 2;
			if (midH === minH || midH === maxH) break;
			const midW = midH * ratio;
			if (this.doesRectangleFit(mainPolygon, midW, midH)) {
				bestFoundHeight = midH;
				minH = midH;
			} else {
				maxH = midH;
			}
		}

		const finalWidth = bestFoundHeight * ratio;
		return {
			width: finalWidth,
			height: bestFoundHeight,
			area: finalWidth * bestFoundHeight,
			ratio: ratio,
		};
	}

	static findLargestApproximateAABB(
		mainPolygon: Feature<Polygon>
	): RectangleResult {
		const ratiosToTest = [
			1 / 5,
			1 / 4,
			1 / 3,
			1 / 2,
			2 / 3,
			1,
			3 / 2,
			2,
			3,
			4,
			5,
		];
		let bestRectangle: RectangleResult = {
			width: 0,
			height: 0,
			area: 0,
			ratio: 0,
		};
		const polygonBbox = bbox(mainPolygon);
		const maxPossibleHeight = polygonBbox[3] - polygonBbox[1];

		for (const ratio of ratiosToTest) {
			const resultForRatio = this.findLargestForAspectRatio(
				mainPolygon,
				ratio,
				maxPossibleHeight
			);
			if (resultForRatio.area > bestRectangle.area) {
				bestRectangle = resultForRatio;
			}
		}
		return {
			width: Math.round(bestRectangle.width),
			height: Math.round(bestRectangle.height),
			area: Math.round(bestRectangle.area),
			ratio: bestRectangle.ratio,
		};
	}

	static roundToPrecision(n: number, precision: number) {
		const factor = Math.pow(10, precision);
		return Math.round(n * factor) / factor;
	}

	static roundVec3ToPrecision(vec: Vector3, precision: number) {
		vec.x = RenderUtils.roundToPrecision(vec.x, precision);
		vec.y = RenderUtils.roundToPrecision(vec.y, precision);
		vec.z = RenderUtils.roundToPrecision(vec.z, precision);
	}
}
